/**
 * "The client pressed this tab on purpose" — a one-shot mark that rides one history entry.
 *
 * <p>Several vitrine screens open something by themselves on arrival: Home jumps to the unfinished money
 * step, the top-up screen opens a single live request instead of a list of one. That is right for a cold
 * start or a magic link, and wrong the moment the client pressed the tab — they asked for the screen, not
 * for whatever it decided to show them. `history.state` is what tells the two apart.</p>
 *
 * <p>Extracted because it was about to be copy-pasted into a third file. Two details are easy to lose in a
 * copy and both bite:</p>
 * <ul>
 *   <li><b>Spread the rest of the state.</b> vue-router keeps `current`/`back`/`forward`/`position`/`scroll`
 *       in there; a bare `replaceState({ fromTab: false })` silently breaks scroll restoration and the
 *       router's own back/forward bookkeeping.</li>
 *   <li><b>Quench after reading.</b> The mark survives a page reload, so leaving it set would disable
 *       auto-open for that history entry forever.</li>
 * </ul>
 */

/** Router `state` payload for a deliberate tab/button navigation. */
export const TAB_INTENT = { fromTab: true } as const;

/** Was this entry reached by a deliberate tab press? Reads once and clears the mark. */
export function consumeTabIntent(): boolean {
  const st = window.history.state as Record<string, unknown> | null;
  if (!st || st.fromTab !== true) return false;
  try {
    window.history.replaceState({ ...st, fromTab: false }, '');
  } catch {
    /* a browser that refuses replaceState only costs us one extra auto-open */
  }
  return true;
}
